// Client-side mirror of the stock-management ICCID rules, so the operator sees a
// bad value before the server rejects the whole request.
export const MAX_ICCIDS = 1000;

const ICCID_PATTERN = /^[0-9]{19,20}$/;

export const isIccid = (value) => ICCID_PATTERN.test(value);

// Same layout rules as the server's file reader: newline / comma / semicolon
// separators, optional BOM, optional leading `iccid` header cell, surrounding
// quotes and whitespace trimmed, empty cells skipped, repeats collapsed.
// Pasted text may also be separated by plain spaces or tabs.
export const parseIccids = (text = '', { splitOnWhitespace = false } = {}) => {
  const separator = splitOnWhitespace ? /[\s,;]+/ : /[\r\n,;]+/;
  const cells = text
    .replace(/^\uFEFF/, '')
    .split(separator)
    .map((cell) => cell.trim().replace(/^"+|"+$/g, '').trim())
    .filter(Boolean);
  if (cells[0]?.toLowerCase() === 'iccid') cells.shift();

  const iccids = [...new Set(cells.filter(isIccid))];
  const invalid = [...new Set(cells.filter((cell) => !isIccid(cell)))];
  return { iccids, invalid };
};

export const validateIccidList = ({ iccids, invalid }) => {
  if (invalid.length) return `Not an ICCID: ${invalid.slice(0, 5).join(', ')}${invalid.length > 5 ? '…' : ''}`;
  if (!iccids.length) return 'No ICCID found';
  if (iccids.length > MAX_ICCIDS) return `At most ${MAX_ICCIDS} ICCIDs per request (found ${iccids.length})`;
  return null;
};

// ICCIDs exceed Number precision, so range width is computed with BigInt.
export const validateIccidRange = (minIccid, maxIccid) => {
  if (!isIccid(minIccid) || !isIccid(maxIccid)) return 'Both bounds must be 19–20 digits';
  const width = window.BigInt(maxIccid) - window.BigInt(minIccid) + window.BigInt(1);
  if (width < window.BigInt(1)) return 'Max must not be lower than min';
  if (width > window.BigInt(MAX_ICCIDS)) return `A range may cover at most ${MAX_ICCIDS} ICCIDs`;
  return null;
};

export const ICCID_ERROR_LABELS = {
  ICCID_NOT_FOUND: 'Not in stock',
  ICCID_ALREADY_ASSIGNED: 'Held by another organization',
  ICCID_ORGANIZATION_MISMATCH: 'No longer in the source organization',
  ICCID_NOT_ASSIGNABLE: 'Not available',
  ICCID_ALREADY_RESERVED: 'Reserved — cannot unassign',
};
